/**
 * useLearningInbox — AI 학습함 조회/답변/승인 훅. V1257 (작업 #4)
 *
 * unread 배지는 useCallNotifications 의 polling 패턴 재사용.
 */

import { useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  learningInboxApi,
  type LearningInboxAnswerRequest,
} from "@/api/learning-inbox";
import { useEventSource } from "@/hooks/useEventSource";
import { useAuthStore } from "@/stores/authStore";

const LIST_KEY = "learning-inbox";
const UNREAD_KEY = "learning-inbox-unread-count";
const DETAIL_KEY = "learning-inbox-detail";

/** 리스트 조회 */
export function useLearningInboxList() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  return useQuery({
    queryKey: [LIST_KEY],
    queryFn: () => learningInboxApi.list(),
    enabled: isAuthenticated,
    staleTime: 30_000,
  });
}

/** 상세 조회 */
export function useLearningInboxDetail(id: number | null) {
  return useQuery({
    queryKey: [DETAIL_KEY, id],
    queryFn: () => learningInboxApi.detail(id as number),
    enabled: id !== null,
  });
}

/**
 * unread 배지 카운트 — 폴링 + SSE invalidate (useCallNotifications 패턴).
 */
export function useLearningInboxUnreadCount() {
  const queryClient = useQueryClient();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  const query = useQuery({
    queryKey: [UNREAD_KEY],
    queryFn: () => learningInboxApi.unreadCount(),
    enabled: isAuthenticated,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  // 신규 미응대 감지 SSE → 배지 즉시 갱신
  // backend SseNotificationService.notifyLearningInboxDigest 발행 이벤트명과 일치
  const handleSse = useCallback(
    (message: { type?: string }) => {
      if (message.type === "LEARNING_INBOX_DIGEST") {
        queryClient.invalidateQueries({ queryKey: [UNREAD_KEY] });
        queryClient.invalidateQueries({ queryKey: [LIST_KEY] });
      }
    },
    [queryClient],
  );
  useEventSource("notification", handleSse, { enabled: isAuthenticated });

  return query.data ?? 0;
}

/** 모든 학습함 캐시 무효화 (답변/승인/거부 후) */
function useInvalidateLearningInbox() {
  const queryClient = useQueryClient();
  return useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [LIST_KEY] });
    queryClient.invalidateQueries({ queryKey: [UNREAD_KEY] });
    queryClient.invalidateQueries({ queryKey: [DETAIL_KEY] });
  }, [queryClient]);
}

/** 답변/카테고리 저장 (ORG_POWER+) */
export function useAnswerLearningInbox() {
  const invalidate = useInvalidateLearningInbox();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: number;
      data: LearningInboxAnswerRequest;
    }) => learningInboxApi.answer(id, data),
    onSuccess: invalidate,
  });
}

/** 승인 → KB 반영 (ORG_ADMIN) */
export function useApproveLearningInbox() {
  const invalidate = useInvalidateLearningInbox();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => learningInboxApi.approve(id),
    onSuccess: () => {
      invalidate();
      // 승인 시 KnowledgeBaseItem 이 INSERT 되므로 KB 목록도 갱신
      queryClient.invalidateQueries({ queryKey: ["knowledge-base"] });
    },
  });
}

/** 거부 (ORG_ADMIN) */
export function useRejectLearningInbox() {
  const invalidate = useInvalidateLearningInbox();
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      learningInboxApi.reject(id, reason),
    onSuccess: invalidate,
  });
}
